/**
 * First run is three pages: revenue (/welcome, /welcome/result), "Where do
 * your buyers find you?" (/new, /new/no-buyers) and the starter test
 * (/experiments/new). /welcome is always first run; the later pages are
 * shared with "New test", so they're first run only when router state says so.
 */
export interface FirstRunState {
  firstRun?: boolean;
  /** The wizard's step, kept in history so browser back moves between its steps. */
  wizardStep?: number;
}

export const FIRST_RUN_STATE: FirstRunState = { firstRun: true };

export const FIRST_RUN_STEPS = ['Revenue', 'Where buyers find you', 'Your first test'] as const;

export type FirstRunStep = 1 | 2 | 3;

export interface FirstRunPosition {
  step: FirstRunStep;
  /** How much of the bar is filled, 0–1. */
  progress: number;
}

export function isFirstRunState(state: unknown): boolean {
  return Boolean((state as FirstRunState | null)?.firstRun);
}

/** Where a page sits in first run, or null when it isn't part of it. */
export function firstRunPosition(pathname: string, state: unknown): FirstRunPosition | null {
  if (pathname === '/welcome') return { step: 1, progress: 1 / 6 };
  if (pathname === '/welcome/result') return { step: 1, progress: 1 / 3 };
  if (!isFirstRunState(state)) return null;
  if (pathname === '/new' || pathname === '/new/no-buyers') return { step: 2, progress: 2 / 3 };
  if (pathname === '/experiments/new') {
    // Once the draft is saved the wizard's own steps take over: setup is done,
    // so no bar and no "Skip setup".
    const wizardStep = (state as FirstRunState).wizardStep ?? 1;
    if (wizardStep >= 3) return null;
    return { step: 3, progress: wizardStep === 2 ? 5 / 6 : 3 / 4 };
  }
  return null;
}
